import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import {
  BodyModalStyle,
  ModalScreen,
  ModalDialog,
  ModalContent,
  ModalContentHeader,
  ModalContentBody,
  ModalContentFooter,
} from './styles';
import { Heading } from '../../typography';
import { ButtonsRow } from '../Button/styles';
import { SvgWrapper, SvgX } from '../../svg';
import { isBodyOverflowing, getScrollbarWidth, focusableElements } from './bodyScroll';
import { useConnect, useActions } from '../../redux/hooks';

export const ModalComponent = ({
  id,
  small,
  size,
  beforeClose,
  buttons,
  title,
  children,
  autoFocus,
  contentMaxHeight,
  contentOverflow,
  hideCloseButton,
}) => {
  const { othersOpened, isOpen, modalProps } = useConnect((s) => ({
    othersOpened: s.modals.opened,
    isOpen: s.modals.opened && s.modals.id === id,
    modalProps: s.modals.id === id ? s.modals.props : undefined,
  }));
  const { actionModalClose } = useActions();

  const contentRef = React.createRef();
  const activeElement = useRef(null);
  const scrollWidth = useMemo(getScrollbarWidth, []);

  // isOpen is needed, eslint hook rules disabled
  // eslint-disable-next-line
  const padRight = useMemo(() => (isBodyOverflowing() ? scrollWidth : 0), [isOpen]);

  // eslint-disable-next-line
  useEffect(() => {
    if (!autoFocus) return;
    if (isOpen) {
      activeElement.current = document.activeElement;
      if (contentRef.current) {
        const focusElems = contentRef.current.querySelectorAll(focusableElements.join(', '));
        if (focusElems && focusElems.length > 0 && focusElems[0].focus) focusElems[0].focus();
      }
    } else if (!othersOpened && activeElement.current && activeElement.current.focus) {
      activeElement.current.focus();
    }
  }, [isOpen]); // eslint-disable-line

  const onClose = useCallback(() => {
    if (typeof beforeClose === 'function') beforeClose();
    actionModalClose();
  }, [beforeClose, actionModalClose]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <>
      <BodyModalStyle padRight={padRight} />
      <ModalScreen role="dialog" tabIndex={-1}>
        <ModalDialog role="document" tabIndex={-1} size={small ? 'small' : size}>
          <ModalContent
            ref={contentRef}
            onKeyDown={(e) => {
              if (e.keyCode === 27) onClose();
            }}
          >
            <ModalContentHeader>
              <Heading noMargin type={3}>
                {typeof title === 'function' ? title(modalProps) : title}
              </Heading>
              {!hideCloseButton && (
                <SvgWrapper>
                  <SvgX onClick={onClose} />
                </SvgWrapper>
              )}
            </ModalContentHeader>
            <ModalContentBody contentOverflow={contentOverflow} maxHeight={contentMaxHeight}>
              {typeof children === 'function' ? children(modalProps) : children}
            </ModalContentBody>
            <ModalContentFooter borderTop={contentMaxHeight} center>
              <ButtonsRow>
                {typeof buttons === 'function' ? buttons(onClose, modalProps) : buttons}
              </ButtonsRow>
            </ModalContentFooter>
          </ModalContent>
        </ModalDialog>
      </ModalScreen>
    </>,
    document.body
  );
};

ModalComponent.propTypes = {
  small: PropTypes.bool,
  hideCloseButton: PropTypes.bool,
  size: PropTypes.string,
  beforeClose: PropTypes.func,
  close: PropTypes.func,
  onClose: PropTypes.func,
  isOpen: PropTypes.bool,
  ignoreOuterClick: PropTypes.bool,
  buttons: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  title: PropTypes.oneOfType([PropTypes.func, PropTypes.string, PropTypes.node]),
  autoFocus: PropTypes.bool,
  contentMaxHeight: PropTypes.string,
  id: PropTypes.string.isRequired,
  props: PropTypes.object,
};

ModalComponent.defaultProps = {
  autoFocus: true,
  size: 'default',
};

export default React.memo(ModalComponent);
